import React, { useEffect, useState, useImperativeHandle, useCallback } from 'react';
import { App, Spin, Space, Button, Tooltip, Card } from 'antd';
import { LoadingOutlined, AimOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet-measure-path';
import { enterFullScreen, exitFullScreen } from '../../utils/full';
import { TILELAYER_IMG_URL, TILELAYER_CIA_URL, SUBDOMAINS, MAPOPTIONS } from '../../utils/constants';
import { getLatLng, array, isEmpty } from '../../utils/utils';
import { plus, minus } from '../../utils/digit';

// import * as turf from '@turf/turf';
import './index.scss';

let map = null
const marker = L.icon({
    iconUrl: require('./image/marker.png'),
    iconSize: [32, 32],
    iconAnchor: [16, 32]
})
    , markerCheck = L.icon({
        iconUrl: require('./image/marker-check.png'),
        iconSize: [32, 32],
        iconAnchor: [16, 32]
    })

const Map = React.forwardRef((props, ref) => {
    const { open, id, width, height, margin, zoom, center } = props
        , [layerPolygon] = useState(L.layerGroup([]))               // 默认多边形图层
        , [layerLine] = useState(L.layerGroup([]))                  // 默认线图层
        , [layerMarker] = useState(L.layerGroup([]))                // 默认标注点图层
        , [layerTrajectory] = useState(L.layerGroup([]))            // 默认绘制轨迹图层
        , [featureGroup] = useState(L.featureGroup([]))             // 临时图层 用于取中心点
        , [loading, setLoading] = useState(false)
        , [isFullscreen, setIsFullscreen] = useState(false) // 是否已设置全屏
        , [locateCircle, setLocateCircle] = useState(null)
        , { notification } = App.useApp()
    // , [mapKey, setMapKey] = useState('d979f223ea462aaa6ee630f890ebd75b')

    // 初始化组件
    useEffect(() => {
        if (open) {
            mounted()
        }
        return () => {
            unmount()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])


    // 创建地图
    const mounted = () => {
        if (map) return
        try {
            showLoading()
            // const mapKey = 'd979f223ea462aaa6ee630f890ebd75b'

            const skymap_key = 'c4eb0fa93790a642fad06f3f5aca6cb9'

            const tileLayerImg = L.tileLayer(TILELAYER_IMG_URL + skymap_key, { subdomains: SUBDOMAINS, zIndex: 1 })
                , tileLayerCia = L.tileLayer(TILELAYER_CIA_URL + skymap_key, { subdomains: SUBDOMAINS, transparent: true, zIndex: 2 })

            map = new L.map(id, {
                ...MAPOPTIONS,
                zoom,
                center,
                layers: [L.layerGroup([tileLayerImg, tileLayerCia])],
                doubleClickZoom: false
            })
                .on('click', e => props.onClick(e))
                .on('zoomend', () => {
                    const el = document.getElementsByClassName('leaflet-control-scale-line')[0]
                    document.getElementById(id).style = `width: ${width}; height: ${height}; --mapwidth: ${el.style?.width}`
                });

            map.on('enterFullscreen', function () {
                console.log('进入全屏');
                setIsFullscreen(true)
                props.onEnterFullscreen()
            })

            map.on('exitFullscreen', function () {
                console.log('退出全屏');
                setIsFullscreen(false)
                props.onExitFullscreen()
            })

            // 用给定的选项创建一个比例尺控件。
            L.control.scale().addTo(map);

            props.onMounted(map)



            // 使用 Geolocation API 定位用户
            if (props.isLocate) {
                // 获取定位当前位置成功回调事件
                map.on('locationfound', (e) => {
                    locateCircle ? locateCircle.setLatLng(e.latlng) : setLocateCircle(L.circleMarker(center, { radius: 200 }).addTo(map).setLatLng(e.latlng))
                    hideLoading()
                })
                // 失败回调
                map.on('locationerror', (e) => {
                    if (locateCircle) {
                        map.stopLocate()
                        map.removeLayer(locateCircle)
                        setLocateCircle(null)
                    }
                    notification.error({
                        message: '提示',
                        placement: 'bottomRight',
                        description: '获取当获取地理位置失败，请稍后再试',
                        duration: 2
                    })
                    hideLoading()
                })
            }

            window.addEventListener('keydown', function (e) {
                e = e || window.event
                if (e.code === 'F11') {
                    ref.current.enterFullscreen()
                    e.stopPropagation()
                } else if (e.code === 'Escape') {

                    ref.current?.exitFullscreen()
                    e.stopPropagation()

                }
            })

        } catch ({ message }) {
            // if (message === 'Map container is already initialized.') {
            //     console.log('mounted', message)
            // }
        }
        hideLoading()
    }

    // 地图卸载
    const unmount = useCallback(() => {
        try {
            if (map) {
                // console.log('unmount', map)
                props.onUnmount()
                map.remove()
                map = null
            }
        } catch ({ message }) {
            // if (message === 'Map container is already initialized.') {
            //     console.log('unmount', message)
            // }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const showLoading = () => {
        setLoading(true)
    }

    const hideLoading = () => {
        setTimeout(() => {
            setLoading(false)
        }, 500)
    }

    useImperativeHandle(ref, () => {
        return {
            mapRef: map,
            map,
            MarkerIcon: [marker, markerCheck],
            setLoading: (value) => {
                setLoading(value)
            },
            /**
             * 传入地块ID，绘制地块多边形
             * @param {*} ids 需要绘制的ID组
             * @param {*} options 配置参数
             * @param {*} onSelect 选择多边形事件
             * @param {*} pmIgnore leaflet.pm 忽视该图形
             */
            drawStrips: async ({
                stripsIds = [],             // 地块ID string | array
                data = [],                  // 坐标      
                mode = "single",            // 渲染类型 单层或多层
                options = {},                  // 多边形显示参数
                selectOptions = {},              // 多边形显示参数
                onSelect = () => false,     // 选中事件
                onEdit = () => { },
                onRemove = () => { },
                onLoad = () => { },
                onClick = () => { },
                onDblclick = false,   // 右键
                maxZoom = 18,               // 地图最大显示层级
                pmIgnore = true,            // 默认不开启编辑功能
                type = 'Polygon',           // 类型  多边形：Polygon | 线：Line | 标注：Marker
                isFitBounds = true,         // 是否根据创建的图形设置地图的中心点
                isClear = true,             // 是否根据类型清空图层的内容
                initialValues = [],         // 默认已选中的地块 默认值，只有初始化
                isMeasure = false,          // 显示测距测面效果
                moving = 'fitBounds',
                regular = false             // 固定
            }) => {

                // 地块ID
                stripsIds = stripsIds ? array(stripsIds) ? stripsIds : [stripsIds] : []
                showLoading()
                // if (dexie.state === 0 && stripsIds.length > 0) {
                //     console.log('地图数据加载未完成')
                //     setTimeout(() => {
                //         ref.current.drawStrips({
                //             stripsIds, data, mode, options, selectOptions, onSelect, onEdit, onRemove, onLoad, onClick, onDblclick, maxZoom, pmIgnore, regular, type, isFitBounds, isClear, initialValues, isMeasure, moving
                //         })
                //     }, 1000)
                //     return
                // }
                // if (stripsIds.length > 0 && (dexie.state === -1 || dexie.count === 0)) {
                //     console.log('没有地块信息')
                //     return
                // }
                // let db
                // if (stripsIds.length > 0) {
                //     db = new Dexie(dexie.databaseName)
                //     db.version(dexie.verno).stores({ strips: "&id, latlngs, area" })
                // }

                // if (type === 'Polygon' && (stripsIds.length > 0 || data.length > 0)) {
                //     // 清空地块图层组
                //     isClear && layerPolygon.clearLayers()
                //     // 单选多边形
                //     if (mode === 'single') {
                //         let latlngs = [], layer = {}
                //         // 传入坐标
                //         if (data.length) {
                //             latlngs = data
                //         } else {
                //             // 传入地块的 ids 根据ID取得对应的坐标
                //             const strips = await db.strips.bulkGet(stripsIds)
                //             latlngs = strips.map(item => item.latlngs)
                //         }

                //         layer = ref.current.addLayer({ latlngs }, options, selectOptions, type, mode, onClick, onSelect, onEdit, onRemove, onDblclick, pmIgnore, regular)
                //         // 图层组添加地块多边形并渲染到地图
                //         layerPolygon.addLayer(layer).addTo(map)
                //         if (isFitBounds) {
                //             // 设置地图的视图 平滑移动地块多边形的矩形边界
                //             map[moving](layer.getBounds(), { maxZoom })
                //         }
                //     }
                //     // 多选多边形
                //     if (mode === 'multiple') {
                //         if (data.length) {

                //         } else {
                //             const res = await db.strips.bulkGet(stripsIds)

                //             let total = 0
                //             res.forEach((record) => {
                //                 const { id, area, latlngs } = record
                //                     // 判断是否默认选中
                //                     , check = initialValues.includes(id)
                //                 if (latlngs.flat().length === 0) {
                //                     console.log(`地块${id}，没有地块坐标信息`)
                //                 } else {
                //                     total = check ? plus(total, Number(area)) : plus(total, 0)
                //                     const layer = ref.current.addLayer({ ...record, check }, options, selectOptions, type, mode, onClick, onSelect, onEdit, onRemove, onDblclick, pmIgnore, regular).addTo(featureGroup)
                //                     if (check) {
                //                         layer.setStyle({
                //                             ...layer.options.selectOptions
                //                         })
                //                     }
                //                     layerPolygon.addLayer(layer)
                //                 }
                //             })
                //             if (initialValues.length) {
                //                 // 回调事件
                //                 onSelect({
                //                     value: initialValues,
                //                     total
                //                 })
                //             }

                //             layerPolygon.addTo(map)

                //             if (isFitBounds && !isEmpty(featureGroup._layers)) {
                //                 map[moving](featureGroup.getBounds(), { maxZoom })
                //             }

                //             featureGroup.clearLayers()
                //         }
                //     }

                //     if (isMeasure) {
                //         layerPolygon.getLayers().forEach(NewClass => {
                //             NewClass.showMeasurements({
                //                 showDistances: false
                //             })
                //         })
                //     }
                // }

                if (type === 'Line' && data.length > 0) {
                    // 清空地块图层组
                    isClear && layerLine.clearLayers()
                    if (mode === 'single') {

                    }

                    if (mode === 'multiple') {
                        data.forEach((record) => {
                            const layer = ref.current.addLayer(record, options, selectOptions, type, mode, onClick, onSelect, onEdit, onRemove, onDblclick, pmIgnore).addTo(featureGroup)
                            layerLine.addLayer(layer)
                        })
                        isFitBounds && map[moving](featureGroup.getBounds(), { maxZoom })
                        featureGroup.clearLayers()
                    }
                    if (isMeasure) {
                        layerLine.addTo(map).getLayers().forEach(NewClass => {
                            NewClass.showMeasurements()
                        })
                    } else {
                        layerLine.addTo(map)
                    }
                }

                if (type === 'Marker' && data.length > 0) {
                    if (isClear) {
                        // 清空图层时，保留只读属性的标注点
                        layerMarker.getLayers().forEach(NewClass => {
                            if (NewClass.options.readonly === false) {
                                map.removeLayer(NewClass)
                            }
                        })
                    }
                    if (mode === 'single') {

                    }
                    if (mode === 'multiple') {
                        data.forEach((record) => {
                            const layer = ref.current.addLayer(record, options, selectOptions, type, mode, onClick, onSelect, onEdit, onRemove, onDblclick, pmIgnore).addTo(featureGroup)
                            layerMarker.addLayer(layer)
                        })
                        layerMarker.addTo(map)
                        isFitBounds && map[moving](featureGroup.getBounds(), { maxZoom })
                        featureGroup.clearLayers()
                    }
                }

                hideLoading()
                onLoad({ layerPolygon, layerLine, layerMarker })
                return { layerPolygon, layerLine, layerMarker }
            },
            /**
             * 根据类型创建单个图层
             * @param {*} latlngs 
             * @param {*} type 
             * @param {*} onSelect 
             * @param {*} pmIgnore 
             * @param {*} maxZoom 
             */
            addLayer: function (record, options = {}, selectOptions = {}, type = 'Polygon', mode = 'single', onClick = () => false, onSelect = () => false, onEdit = () => { }, onRemove = () => { }, onDblclick = false, pmIgnore = true, regular = false) {
                if (type === 'Polygon') {
                    const polygonOptions = Object.assign(props.path[type].options, options, record.options)
                    const polygon = L.polygon(record.latlngs, {
                        ...polygonOptions,
                        check: record.check,
                        id: record.id,
                        area: record.area || 0,
                        pmIgnore,
                        options: polygonOptions,
                        selectOptions: Object.assign(props.path[type].selectOptions, selectOptions),
                        regular
                    })

                    if (mode === 'single') {
                        polygon.on('click', ({ target }) => {
                            onClick({
                                record,
                                layer: target
                            })
                            onSelect({
                                layer: target,
                                value: [],
                                total: 0
                            })
                            // 编辑多边形  
                            if (!pmIgnore) {
                                target.setStyle({
                                    ...target.options.selectOptions
                                })
                                target.pm.enable()
                                target.on("pm:edit", ({ target }) => {
                                    const value = getLatLng(target)
                                    value.flat().length ? onEdit({ layer: target, latlngs: value }) : onRemove(target)
                                })
                            }
                        })
                    }

                    if (mode === 'multiple') {
                        polygon.on('click', ({ target }) => {
                            // 开启编辑地块多边形
                            const { id, options, selectOptions, area = 0, check } = target.options
                                // 获取当前layerPolygon._layers图层内所有多边形options.check为选中的id
                                , { value = [], total = 0 } = ref.current.getSelectingDraw()

                            onClick({
                                record,
                                layer: target
                            })

                            // 选中当前多边形的返回值是否可选择
                            const isCheck = onSelect({
                                id,
                                checked: !check,
                                value: check ? value.filter(v => v !== id) : [id, ...value],
                                total: check ? minus(total, Number(area)) : plus(total, Number(area)),
                                layer: target
                            })

                            // 编辑多边形  
                            if (!pmIgnore) {
                                target.pm.enableLayerDrag() // 启用图层拖动
                                target.pm.enable()
                                target.on("pm:edit", ({ target }) => {
                                    const value = getLatLng(target)
                                    // console.log(ringArea(target.getLatLngs()[0]))
                                    value.flat().length ? onEdit({ record, layer: target, latlngs: value }) : onRemove(record)
                                })
                            }

                            if (isCheck) {
                                // 可选时，修改当前多边形的颜色与选中状态
                                // 可选时，修改当前多边形的颜色与选中状态
                                const changeOptions = !check ? selectOptions : options
                                target.setStyle({
                                    ...changeOptions,
                                    check: !check
                                })
                            }
                        })
                        // 双击事件
                        if (typeof onDblclick === 'function') {
                            polygon.on('dblclick', e => onDblclick(e))
                        }
                    }
                    return polygon
                }

                if (type === 'Line') {
                    const lineOptions = Object.assign(props.path[type].options, options, record.options)
                    const polygon = L.polyline(record.latlngs, {
                        ...lineOptions,
                        options: lineOptions,
                        selectOptions,
                    }).on('click', ({ target }) => {
                        onClick({
                            record,
                            layer: target
                        })
                        // 选择事件 返回
                        onSelect({ record, layer: target, layerLine })
                        // 编辑线 
                        if (!pmIgnore) {
                            target.pm.enable()
                            target.on("pm:edit", ({ target }) => {
                                const value = getLatLng(target)
                                value.flat().length ? onEdit({ record, layer: target, latlngs: value }) : onRemove(record)
                            })
                        }
                    })
                    return polygon
                }

                if (type === 'Marker') {
                    const { latlng, key = null, content, icon = props.pathOptions[type].icon, iconCheck = props.pathOptions[type].iconCheck, tooltip = {}, readonly = false, permanent = true } = record
                    const polygon = L.marker(latlng, {
                        icon,
                        readonly,
                        check: false,
                        key,
                        icons: [icon, iconCheck]
                    }).bindTooltip(content, {
                        ...props.pathOptions[type].tooltip,
                        ...tooltip,
                        permanent
                    }).on('click', ({ target }) => {
                        onClick({
                            record,
                            layer: target
                        })
                        // 清空已选样式和状态
                        Object.keys(layerMarker._layers).some(key => {
                            const layer = layerMarker._layers[key]
                            return layer.options.check && (layer.setIcon(layer.options.icons[0]).options.check = false)
                        })
                        // 设置当前选中的样式和状态
                        target.setIcon(target.options.icons[1]).options.check = true
                        // 选择事件 返回
                        onSelect({ record, layer: target })
                        if (!pmIgnore) {
                            // 开启编辑  target.pm.disable() 结束
                            target.pm.enable()
                            target.on("pm:edit", ({ target }) => {
                                const latlng = getLatLng(target)
                                onEdit({ record, layer: target, latlng })
                            })
                            target.on("pm:remove", () => {
                                onRemove(record)
                            })
                        }
                    })
                    return polygon
                }

            },
            /**
             * 外部调接口在地图中创建 多边形，线， 标注点
             * @param {*} type Polygon | Line | Marker
             */
            createDraw: function ({
                type = 'Polygon',
                options = {},
                onCreate = false,
                onEdit = () => { },
                onRemove = () => { },
                isMeasure = true              // 显示测量值
            }) {

                map.pm.setLang('zh')
                if (type === 'Polygon') {
                    map.pm.enableDraw(type, { pathOptions: { ...props.pathOptions[type], ...options } })
                }
                if (type === 'Line') {
                    map.pm.enableDraw(type, { pathOptions: { ...props.pathOptions[type], ...options } })

                }
                if (type === 'Marker') {
                    map.pm.enableDraw(type, {
                        pathOptions: { ...props.pathOptions[type], ...options },
                        markerStyle: { icon: markerCheck }
                    })
                }

                map.on('pm:create', ({ layer }) => {
                    if (isMeasure && typeof layer.showMeasurements === 'function') {
                        layer.showMeasurements()
                    }
                    const value = getLatLng(layer)
                        , params = type === 'Marker' ? { layer, latlng: value } : { layer, latlngs: value }
                    typeof onCreate === 'function' && onCreate(params)
                    onEdit(params)
                    // 关闭创建
                    map.pm.disableDraw()
                    // 开启编辑
                    layer.pm.enable()
                    layer.on("pm:edit", ({ target }) => {
                        const value = getLatLng(target)
                            , params = type === 'Marker' ? { layer: target, latlng: value } : { layer: target, latlngs: value }

                        value.flat().length ? onEdit(params) : onRemove(layer)
                    })
                    if (type === 'Marker') {
                        layer.on("pm:remove", () => {
                            onRemove(layer)
                        })
                    }
                })
            },
            /**
             * 清空默认多边形，线， 标注点图层内容
             * @param {*} isAll false 时，保留readonly属性的图层
             */
            clearDraw: (isAll = true) => {
                layerPolygon.clearLayers()
                layerLine.clearLayers()
                layerTrajectory.clearLayers()
                if (isAll) {
                    layerMarker.clearLayers()
                } else {
                    // 清空图层时，保留只读属性的标注点
                    layerMarker.getLayers().forEach(NewClass => {
                        if (NewClass.options.readonly === false) {
                            map.removeLayer(NewClass)
                        }
                    })
                }
            },
            /**
             * 获取当前layerPolygon._layers图层内所有多边形options.check为选中的id列表与统计面积
             * @returns 
             */
            getSelectingDraw: () => {
                let value = [], total = 0
                Object.keys(layerPolygon._layers).forEach(key => {
                    const layer = layerPolygon._layers[key]
                        , { id, check, area = 0 } = layer.options
                    if (check) {
                        value.push(id)
                        total = plus(total, Number(area))
                    }
                })
                return { value, total }
            },
            /**
             * 设置当前layerPolygon._layers图层内所有多边形options.check为选中 或 传空为取消选中
             * @param {*} ids 选中的ID 
             */
            selectingDraw: ({ ids = [], selectColor = null }) => {
                let total = 0
                Object.keys(layerPolygon._layers).forEach(key => {
                    const layer = layerPolygon._layers[key]
                        , { id, area = 0, options, selectOptions, regular } = layer.options
                        , isCheck = ids.includes(id)
                    if (regular) return
                    const polygonOptions = isCheck ? selectColor ? { color: selectColor, fillColor: selectColor } : selectOptions : options
                    layer.setStyle({
                        ...polygonOptions,
                        check: isCheck
                    })
                    if (isCheck) {
                        total = plus(total, Number(area))
                    }
                })
                return { value: ids, total }
            },
            getPolygon: () => {
                return layerPolygon
            },
            getTrajectory: () => {
                return layerTrajectory
            },
            getLine: () => {
                return layerLine
            },
            getMarker: () => {
                return layerMarker
            },
            /**
             * 设置线图层layerLine样式 
             */
            setLineStyle: (options = {}) => {
                // 取线图层内所有线图形
                layerLine.getLayers().forEach(NewClass => {
                    // 取得线原始级别
                    const { level } = NewClass.options
                        // 重新计算线的粗细
                        , weight = options.weight ? level + options.weight : level
                    NewClass.setStyle({
                        ...NewClass.options,
                        ...options,
                        weight
                    })
                })
            },
            // 地图通知
            mapNotification: (description = '', type = 'info') => {
                notification[type]({
                    key: 'map-notification',
                    message: '提示',
                    description,
                    placement: 'bottomRight',
                    duration: 2
                })
            },
            enterFullscreen: (element = document.getElementById('map-card')) => {
                if (map._isFullscreen) {
                    return
                }
                // 进入全屏
                enterFullScreen(element)
                element.classList.add('fullscreen')
                map.fire('enterFullscreen');
                map._exitFired = false;
                map._isFullscreen = true;
                map.invalidateSize()
            },
            exitFullscreen: (element = document.getElementById('map-card')) => {
                if (!map._isFullscreen) {
                    return
                }
                console.log(11)
                // 退出全屏
                exitFullScreen(element)
                element.classList.remove('fullscreen')
                map.fire('exitFullscreen');
                map._exitFired = true;
                map._isFullscreen = false;
                map.invalidateSize()
            },
            toggleFullscreen: () => {
                // 切换全屏
                const page = document.getElementById('map-card')
                map._isFullscreen ? ref.current.exitFullscreen(page) : ref.current.enterFullscreen(page)
            },
            /**
             * 绘制GPS设备工作轨迹 
             */
            trajectoryDraw: ({
                dataSource = [],
                first = null,
                last = null,
                color = ['green', 'red'],
                machine = {},
                type = '',
                // mode = "single",            // 渲染类型 单层或多层
                moving = 'fitBounds',        // 地图移动类型
                isFitBounds = true,         // 是否根据创建的图形设置地图的中心点
                isClear = true,             // 是否根据类型清空图层的内容
                isMeasure = false,          // 显示测距测面效果
                maxZoom = 18,               // 地图最大显示层级
                onLoad = () => { },
                onSelect = () => false,     // 选中事件
                weight = 1,
                tooltip = '',
                key = null,
                firstTime = '',
                lastTime = '',
                permanent = false
            }) => {
                showLoading()
                // 清空地块图层组
                isClear && layerTrajectory.clearLayers()
                // 根据状态分组轨迹数据
                const latlngs = [
                    dataSource.filter(record => record.state.toString() === '0').map(record => record.coords),
                    dataSource.filter(record => record.state.toString() === '1').map(record => record.coords)
                ]
                latlngs.forEach((latlng, index) => {
                    const layer = L.polyline(latlng, {
                        color: color[index],
                        weight,
                        key,
                        state: index,
                        type: 'Line'
                    }).on('click', (e) => {
                        console.log(e)
                    })
                    layerTrajectory.addLayer(layer)
                })

                layerTrajectory.addTo(map)

                // 根据轨迹图层，定位地图中心点
                if (isFitBounds) {
                    setTimeout(() => {
                        layerTrajectory.getLayers().forEach(NewClass => NewClass.addTo(featureGroup))
                        map.fitBounds(featureGroup.getBounds())
                        featureGroup.clearLayers()
                        hideLoading()
                    }, 500)
                } else {
                    hideLoading()
                }


                // 起点
                if (first) {
                    const firstMarker = L.marker(first, {
                        icon: props.pathOptions['Marker'].icon
                    }).bindTooltip('设备：' + tooltip + '<br>起点：' + firstTime, {
                        ...props.pathOptions['Marker'].tooltip,
                        permanent
                    })
                    layerTrajectory.addLayer(firstMarker)
                }

                // 终点
                if (last) {
                    const firstMarker = L.marker(last, {
                        icon: props.pathOptions['Marker'].icon,
                        key
                    }).bindTooltip('设备：' + tooltip + '<br>终点：' + lastTime, {
                        ...props.pathOptions['Marker'].tooltip,
                        permanent
                    })
                    layerTrajectory.addLayer(firstMarker)
                }

                // layerTrajectory.getLayers().forEach(async NewClass => {
                //     if (NewClass.options.state === 0) {
                //         const points = turf.points(getLatLng(NewClass).flat())
                //             , db = new Dexie(dexie.databaseName)
                //             , result = await db.open()
                //             , strips = await result.table('strips').toArray()
                //             , ids = []
                //         let area = 0
                //         strips.forEach(item => {
                //             try {
                //                 var polygon = turf.polygon(item.latlngs);
                //                 var ptsWithin = turf.pointsWithinPolygon(points, polygon);
                //                 if (ptsWithin.features.length > 22) {
                //                     ids.push(item.id)
                //                     area = plus(area, item.area)
                //                 }
                //             } catch (error) {
                //                 console.log(item, error)
                //             }
                //         })
                //         ref.current.drawStrips({
                //             mode: 'single',
                //             stripsIds: ids,
                //             isFitBounds: false
                //         })
                //         console.log(key, ids, area * 0.0015)
                //     }
                // })

                return layerTrajectory
            },
            /**
             * 设置轨迹颜色 
             * @param {*} key 轨迹ID
             * @param {*} color 颜色
             * @param {*} state 状态
             */
            setTrajectoryColor: (key, color, state) => {
                layerTrajectory.getLayers().forEach(NewClass => {
                    const { options } = NewClass
                    if (options.state === state && options.key === key && options.type === 'Line') {
                        NewClass.setStyle({ color })
                    }
                })
            },
            /**
             * 设置轨迹大小
             * @param {*} weight 
             * @param {*} key 轨迹ID
             */
            setTrajectoryWeight: (weight, key = null) => {
                layerTrajectory.getLayers().forEach(NewClass => {
                    const { options } = NewClass
                    if ((key && options.key === key) || key === null) {
                        if (options.type === 'Line') {
                            NewClass.setStyle({ weight })
                        }
                    }
                })
            },
            /**
             * 
             * @param {*} key 设备key
             * @param {*} latLng 坐标
             * @param {*} state 状态
             * @param {*} pre 前一次坐标
             */
            realTimeTrajectory: (key, latLng, state, pre) => {
                // 锁定设备 为地图中心点渲染。
                layerMarker.getLayers().forEach(NewClass => {
                    if (key === NewClass.options.key) {
                        NewClass.setLatLng(latLng)
                        if (NewClass.options.lock) {
                            map.flyTo(latLng, 18)
                        }
                    }
                })
                layerTrajectory.getLayers().some(NewClass => {
                    const { options } = NewClass
                    if (key === options.key && state === options.state) {
                        const LatLngs = NewClass.getLatLngs()
                            , last = LatLngs[LatLngs.length - 1]
                            , lastLatLng = last[last.length - 1]
                        // 对比最后一次坐标是否一致 则区分是否分段渲染轨迹
                        if (pre === lastLatLng) {
                            NewClass.addLatLng(latLng)
                        } else {
                            NewClass.setLatLngs([...LatLngs, [pre, latLng]])
                        }
                        return true
                    } else {
                        return false
                    }
                })
            },
            lockMarker: (key) => {
                layerMarker.getLayers().forEach(NewClass => {
                    NewClass.options = { ...NewClass.options, lock: key === NewClass.options.key }
                })

                if (!key) {
                    layerMarker.getLayers().forEach(NewClass => NewClass.addTo(featureGroup))
                    map.fitBounds(featureGroup.getBounds())
                    featureGroup.clearLayers()
                }
            },
            trajectoryEmpty: () => {
                layerTrajectory.clearLayers()
            },
            focus: () => {
                document.getElementById(id).focus()
            }
        }
    }, [ref, props, id, notification, featureGroup, layerPolygon, layerLine, layerMarker, layerTrajectory])

    return open ? (
        <>
            <Card
                title={props.title}
                bodyStyle={{ padding: 0 }}
                extra={props.extra}
                id="map-card"
                style={{ ...props.cardStrye, margin }}
            >
                <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 64 }} spin />}
                    spinning={loading || props.spinning}
                    style={{ height, maxHeight: height }}
                >
                    <div
                        ref={ref}
                        key={id}
                        id={id}
                        style={{ width, height, '--mapwidth': '77px' }}
                        className="map"
                        tabIndex={0}
                    >

                        <div id="map-footer">
                            <div></div>
                            <Space.Compact>
                                {
                                    props.isLocate ? (
                                        <Tooltip
                                            title={locateCircle ? '清除定位当前位置' : '定位当前自己的位置'}
                                            placement='topRight'
                                            getPopupContainer={() => document.getElementById('map-footer')}
                                        >
                                            <Button
                                                icon={<AimOutlined />}
                                                type={locateCircle ? 'primary' : 'default'}
                                                onClick={() => {
                                                    if (locateCircle) {
                                                        map.stopLocate()
                                                        map.removeLayer(locateCircle)
                                                        setLocateCircle(null)
                                                        return
                                                    }
                                                    showLoading()
                                                    map.locate({
                                                        watch: true,
                                                        setView: true,
                                                        enableHighAccuracy: true,
                                                        maxZoom: 17
                                                    })
                                                }}
                                            />
                                        </Tooltip>
                                    ) : null
                                }
                                {
                                    props.isFullscreen ? (
                                        <Tooltip
                                            title={!isFullscreen ? '显示全屏' : '退出全屏模式'}
                                            placement='topRight'
                                            getPopupContainer={() => document.getElementById('map-footer')}
                                        >
                                            <Button
                                                icon={!isFullscreen ? <FullscreenOutlined /> : <FullscreenExitOutlined />}
                                                type={isFullscreen ? 'primary' : 'default'}
                                                onClick={() => ref.current.toggleFullscreen()}
                                            />
                                        </Tooltip>
                                    ) : null
                                }
                            </Space.Compact>
                        </div>

                    </div>
                </Spin>
                {props.children}
            </Card >
        </>
    ) : null
})

Map.defaultProps = {
    // dexie: {// 数据是否已经准备完成
    //     databaseName: null,
    //     verno: null,
    //     count: 0,
    //     state: 0
    // },
    open: true,
    cardStrye: {},
    id: 'content',
    zoom: 10,
    center: [29.72188979, 118.34543725],
    width: '100%',
    height: '100px',
    margin: 16,
    children: null,
    title: null,
    extra: null,
    extraStyle: {},
    isLocate: false, // 获取当前位置
    isFullscreen: true, // 全屏
    spinning: false,
    onClick: (e) => {
        // 地图单击事件
        // console.log('onClick')
        // e.stopPropagation()
    },
    onMounted: (map) => {
        // 地图加载完成事件
        // console.log('onLoad')
    },
    onUnmount: () => {
        // 地图卸载回调事件
        // console.log('onUnload')
    },
    onEnterFullscreen: () => {

    },
    onExitFullscreen: () => {

    },
    path: {
        Polygon: {
            options: {
                color: '#a0d911',
                opacity: 0.5,
                fillColor: '#a0d911',
                fillOpacity: 0.5,
                weight: 1,
            },
            selectOptions: {
                color: '#f5222d',
                opacity: 0.5,
                fillColor: '#f5222d',
                fillOpacity: 0.5,
                weight: 1,
            }
        },
        Line: {
            options: {
                color: '#a0d911',
                weight: 5,
                opacity: .7
            },
            selectOptions: {
                color: '#f5222d',
                weight: 5,
                opacity: 1
            }
        },
        Marker: {

        }
    },
    pathOptions: {
        'Polygon': {
            color: '#a0d911',
            opacity: 0.5,
            fillColor: '#a0d911',
            fillOpacity: 0.5,
            weight: 1,
            selectColor: '#f5222d'
        },
        'Line': {
            color: '#a0d911',
            weight: 5,
            opacity: 1
        },
        'Marker': {
            icon: marker,
            iconCheck: markerCheck,
            tooltip: {
                direction: 'top',
                permanent: true,
                offset: L.point(0, -32)
            }
        }
    },
}

export default Map
